import { useCallback, useMemo } from 'react'
import { toast } from '@sales-center/components'
import type { FormikErrors } from 'formik'

import { useAppDispatch } from '@user/store/hooks'
import { addCustomField, updateCustomField } from '@user/store/customFields'
import type {
  OptionModel,
  CustomFieldFormProps,
  CustomFieldFormModel,
} from '../typings'

function validateOptionArray(options: OptionModel[]): string[] {
  const errors: string[] = []

  const dict: Record<string, number[]> = {}

  options.map(({ name }, idx) => {
    const key = name.toLowerCase().trim()
    dict[key] = dict[key] ? [...dict[key], idx] : [idx]
  })

  Object.entries(dict).map(([key, idx]) => {
    if (key === '') {
      idx.map((i) => (errors[i] = 'Option cannot be blank'))
    } else if (idx.length > 1) {
      idx.map((i) => (errors[i] = 'Options must be unique'))
    }
  })

  return errors
}

export function useActions(props: CustomFieldFormProps) {
  const { onClose, initialValues, customFields } = props

  const dispatch = useAppDispatch()

  const isUpdate = initialValues?.id !== undefined

  const onSave = useCallback(
    async (model: CustomFieldFormModel) => {
      const { id, title, fieldType, options } = model

      const action = isUpdate ? updateCustomField : addCustomField

      const result = await dispatch(
        action({
          id,
          options,
          name: title,
          type: fieldType,
        }),
      )

      if (action.fulfilled.match(result)) {
        toast.success(`Custom Field ${isUpdate ? 'updated' : 'created'}`)
        onClose()
      }
    },
    [dispatch, isUpdate, onClose],
  )

  const fieldNamesInUse = useMemo(
    () =>
      customFields
        .filter((x) => x.id + '' !== initialValues?.id)
        .map((x) => x.name.toLowerCase()),
    [customFields, initialValues?.id],
  )

  const onValidate = useCallback(
    (model: CustomFieldFormModel) => {
      const { title, fieldType, options } = model

      const errors: FormikErrors<CustomFieldFormModel> = {}

      if (fieldNamesInUse.includes(title.toLowerCase()))
        errors.title = 'Field titles must be unique'
      else if (title === '') errors.title = 'Required'

      if (!fieldType) errors.fieldType = 'Required'

      if (
        fieldType === 'DropdownMultiSelect' ||
        fieldType === 'DropdownSingleSelect'
      ) {
        if (options.length === 0) {
          errors.options = 'Dropdowns must have at least one option'
        } else {
          const optionErrors = validateOptionArray(options)
          if (optionErrors.length > 0) errors.options = optionErrors
        }
      }

      return errors
    },
    [fieldNamesInUse],
  )

  return {
    onSave,
    isUpdate,
    onValidate,
  }
}
